import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { UserPlus, Users, Loader2, KeyRound, IdCard, Pencil, Trash2 } from "lucide-react";

export default function VendorsManagement() {
  const [vendors, setVendors] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null); // NULL = Crear, NUMERO = Editar

  const [form, setForm] = useState({
    email: "",
    password: "",
    nombre: "",
    apellido_paterno: "",
    apellido_materno: "",
    id_dueno: ""
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [vRes, oRes] = await Promise.all([
        api.get("/vendors"),
        api.get("/owners")
      ]);
      setVendors(vRes.data);
      setOwners(oRes.data);
    } catch (err) {
      toast.error("Error de conexión", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ email: "", password: "", nombre: "", apellido_paterno: "", apellido_materno: "", id_dueno: "" });
    setOpen(true);
  };

  const openEditModal = (v) => {
    setEditingId(v.id);
    setForm({
      email: v.email || "",
      password: "", // Se deja vacío para que solo se actualice si escribe algo nuevo
      nombre: v.nombre || "",
      apellido_paterno: v.apellido_paterno || "",
      apellido_materno: v.apellido_materno || "",
      id_dueno: v.id_dueno ? String(v.id_dueno) : ""
    });
    setOpen(true);
  };

  const handleDelete = async (id, nombre) => {
    if (!confirm(`¿Estás seguro de que deseas eliminar permanentemente al vendedor ${nombre} y sus accesos?`)) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendedor eliminado correctamente");
      loadData();
    } catch (err) {
      toast.error("Error al eliminar", { description: formatApiError(err) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_dueno) return toast.error("Debes seleccionar un dueño");
    
    setSubmitting(true);
    try {
      if (editingId) {
        // Modo Edición
        await api.patch(`/vendors/${editingId}`, form);
        toast.success("Perfil de vendedor actualizado");
      } else {
        // Modo Creación
        await api.post("/vendors", form);
        toast.success("Vendedor y perfil registrados");
      }
      setOpen(false);
      loadData();
    } catch (err) {
      toast.error("Error al guardar", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <h3 className="text-xl font-bold flex items-center gap-2.5">
          <IdCard size={22} className="text-slate-500" /> Administración del Equipo de Ventas
        </h3>
        <Button onClick={openCreateModal} className="gap-2 bg-slate-900 h-10 px-5 shadow-md shadow-slate-900/10">
          <UserPlus size={17} /> Nuevo Vendedor
        </Button>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm">
        <Table>
          <TableHeader className="bg-slate-50 border-b border-slate-200">
            <TableRow>
              <TableHead className="w-20 pl-6">ID</TableHead>
              <TableHead>Nombre Completo</TableHead>
              <TableHead>Email de Acceso</TableHead>
              <TableHead>Dueño Responsable</TableHead>
              <TableHead className="text-right pr-6">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center">
                  <Loader2 className="animate-spin inline mr-3 text-slate-400" size={20} />
                  <span className="text-slate-500">Cargando equipo de ventas...</span>
                </TableCell>
              </TableRow>
            ) : vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-32 text-center text-slate-400 italic">
                  No hay vendedores registrados todavía.
                </TableCell>
              </TableRow>
            ) : (
              vendors.map((v) => {
                const owner = owners.find(o => String(o.id) === String(v.id_dueno));
                return (
                  <TableRow key={v.id} className="hover:bg-slate-50/50">
                    <TableCell className="font-mono text-xs text-slate-500 pl-6">{v.id}</TableCell>
                    <TableCell className="font-semibold text-slate-900">{v.nombre_completo}</TableCell>
                    <TableCell className="text-slate-600 font-mono text-sm">{v.email}</TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {owner ? (
                        <span className="font-medium text-slate-800">
                          {owner.nombre} {owner.apellido_paterno}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">No asignado</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(v)} title="Editar Vendedor">
                          <Pencil size={16} className="text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(v.id, v.nombre)} title="Eliminar Vendedor">
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden rounded-2xl">
          <form onSubmit={handleSubmit}>
            <DialogHeader className="p-7 pb-3 border-b border-slate-100">
              <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                {editingId ? `Editar Vendedor #${editingId}` : "Registro Integral de Vendedor"}
              </DialogTitle>
              <p className="text-slate-500 text-sm mt-1">
                {editingId ? "Actualiza la información personal o cambia la contraseña de acceso." : "Ingresa los datos de acceso y el perfil del vendedor en un solo paso."}
              </p>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              
              <div className="p-7 space-y-5 bg-slate-50/50 border-r border-slate-100">
                <h4 className="font-bold text-base flex items-center gap-2 text-slate-900 border-b border-slate-200 pb-2 mb-4">
                  <KeyRound size={18} className="text-blue-600" />
                  1. Cuenta (Acceso al sistema)
                </h4>
                
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="font-medium">Correo Electrónico *</Label>
                  <Input id="email" type="email" required placeholder="vendedor@ejemplo.com" className="bg-white h-11" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="font-medium">
                    {editingId ? "Nueva Contraseña (Opcional)" : "Contraseña Temporal *"}
                  </Label>
                  <Input 
                    id="password" 
                    type="text" 
                    required={!editingId} 
                    placeholder={editingId ? "Déjalo en blanco para no cambiarla" : "Contraseña de acceso"} 
                    className="bg-white h-11 font-mono placeholder:text-xs" 
                    value={form.password} 
                    onChange={e => setForm({...form, password: e.target.value})} 
                  />
                </div>
              </div>

              <div className="p-7 space-y-5 bg-white">
                <h4 className="font-bold text-base flex items-center gap-2 text-slate-900 border-b border-slate-200 pb-2 mb-4">
                  <Users size={18} className="text-emerald-600" />
                  2. Perfil Personal
                </h4>
                
                <div className="space-y-1.5">
                  <Label htmlFor="nombre" className="font-medium">Nombre(s) *</Label>
                  <Input id="nombre" required placeholder="Ej. Juan Carlos" className="h-11" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="apellido_paterno" className="font-medium">Apellido Paterno *</Label>
                    <Input id="apellido_paterno" required placeholder="Ej. Pérez" className="h-11" value={form.apellido_paterno} onChange={e => setForm({...form, apellido_paterno: e.target.value})} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="apellido_materno" className="font-medium">Apellido Materno</Label>
                    <Input id="apellido_materno" placeholder="Ej. Gómez" className="h-11" value={form.apellido_materno} onChange={e => setForm({...form, apellido_materno: e.target.value})} />
                  </div>
                </div>
                
                <div className="space-y-1.5 pt-1">
                  <Label htmlFor="dueno" className="font-medium">Asignar a Dueño *</Label>
                  <Select onValueChange={val => setForm({...form, id_dueno: val})} value={form.id_dueno} required>
                    <SelectTrigger id="dueno" className="h-11 bg-white">
                      <SelectValue placeholder="Selecciona un dueño de la lista..." />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.length > 0 ? (
                        owners.map((o) => (
                          <SelectItem key={o.id} value={String(o.id)}>
                            {o.nombre} {o.apellido_paterno} {o.apellido_materno}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-slate-500 text-center">No se encontraron dueños</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="p-7 pt-5 border-t border-slate-100 bg-slate-50/50 gap-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-11 px-6 border-slate-300">
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="h-11 px-8 bg-slate-900 shadow-lg shadow-slate-900/10">
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Guardando...
                  </>
                ) : (
                  editingId ? "Actualizar Vendedor" : "Confirmar y Crear Registro"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}